import { FC, Fragment, FormEvent, ReactNode } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { X } from 'lucide-react';
import { GenerateButton, SecondaryButton } from '@/components/ui/Button';

interface GenerateAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e?: FormEvent) => void;
  title: string;
  description?: string;
  generateButtonLabel?: string;
  isLoading?: boolean;
  children: ReactNode;
}

const GenerateAIModal: FC<GenerateAIModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  description,
  generateButtonLabel = 'Generate',
  isLoading = false,
  children,
}) => {
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-2"
          >
            <DialogPanel className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <DialogTitle className="text-xl font-semibold text-gray-900">{title}</DialogTitle>
                  {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form Content */}
              <form onSubmit={onSubmit}>
                <div className="p-6 space-y-5">{children}</div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <SecondaryButton onClick={onClose} disabled={isLoading}>
                    Cancel
                  </SecondaryButton>
                  <GenerateButton type="submit" loading={isLoading} disabled={isLoading}>
                    {isLoading ? 'Generating...' : generateButtonLabel}
                  </GenerateButton>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GenerateAIModal;
